import React, { useEffect, useState } from "react";
import { api, apiErr, fmtMoney, fmtDate } from "../lib/api";
import { PageHeader, StatusBadge } from "../components/ResourcePage";
import { Card } from "../components/ui/card";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  TrendingUp, TrendingDown, Wallet, Clock, Beef, Sprout, Boxes, Users,
} from "lucide-react";
import { toast } from "sonner";

const PIE = ["#2C5530", "#7A906F", "#D4A373", "#4A8EBA", "#C25934", "#4CAF50"];

function Kpi({ label, value, icon: Icon, tone }) {
  const tones = {
    green: "bg-emerald-50 text-emerald-700", red: "bg-red-50 text-red-600",
    blue: "bg-sky-50 text-sky-700", amber: "bg-amber-50 text-amber-700",
  };
  return (
    <Card className="p-5 transition-all duration-200 hover:shadow-sm" data-testid={`kpi-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-[0.1em] text-muted-foreground">{label}</p>
        <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${tones[tone]}`}><Icon className="h-[18px] w-[18px]" /></div>
      </div>
      <p className="num mt-3 font-display text-2xl font-extrabold tracking-tight">{value}</p>
    </Card>
  );
}

function CountTile({ label, value, icon: Icon }) {
  return (
    <Card className="flex items-center gap-3 p-4">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary text-primary"><Icon className="h-5 w-5" /></div>
      <div>
        <p className="num font-display text-lg font-bold">{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
    </Card>
  );
}

export function Dashboard() {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setD(data)).catch((e) => toast.error(apiErr(e)));
  }, []);

  if (!d) return <div className="py-20 text-center text-muted-foreground">Loading dashboard…</div>;

  const monthLabel = (m) => {
    const [y, mo] = m.split("-");
    return new Date(y, mo - 1).toLocaleDateString("en-US", { month: "short" });
  };
  const chartData = d.monthly.map((x) => ({ ...x, label: monthLabel(x.month) }));

  return (
    <div className="fade-up">
      <PageHeader title="Dashboard" subtitle="Your farm's financial and operational pulse." />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Total Income" value={fmtMoney(d.kpis.income)} icon={TrendingUp} tone="green" />
        <Kpi label="Total Expenses" value={fmtMoney(d.kpis.expense)} icon={TrendingDown} tone="red" />
        <Kpi label="Net Profit" value={fmtMoney(d.kpis.profit)} icon={Wallet} tone="blue" />
        <Kpi label="Outstanding" value={fmtMoney(d.kpis.outstanding)} icon={Clock} tone="amber" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="p-6 lg:col-span-2">
          <h3 className="font-display text-lg font-bold tracking-tight">Income vs Expenses</h3>
          <p className="mb-4 text-xs text-muted-foreground">Last 6 months</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} barGap={6}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
              <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={12} />
              <YAxis tickLine={false} axisLine={false} fontSize={12} tickFormatter={(v) => `₦${v / 1000}k`} />
              <Tooltip formatter={(v) => fmtMoney(v)} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
              <Bar dataKey="income" name="Income" fill="#2C5530" radius={[4, 4, 0, 0]} />
              <Bar dataKey="expense" name="Expense" fill="#D4A373" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6">
          <h3 className="font-display text-lg font-bold tracking-tight">Expense Breakdown</h3>
          <p className="mb-4 text-xs text-muted-foreground">By category</p>
          {d.expense_by_category.length === 0 ? (
            <div className="flex h-[240px] items-center justify-center text-sm text-muted-foreground">No expenses recorded yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={d.expense_by_category} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={2}>
                  {d.expense_by_category.map((_, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmtMoney(v)} />
                <Legend iconType="circle" fontSize={11} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <CountTile label={`Livestock (${d.livestock_head} head)`} value={d.counts.livestock} icon={Beef} />
        <CountTile label="Crops" value={d.counts.crops} icon={Sprout} />
        <CountTile label="Inventory items" value={d.counts.inventory} icon={Boxes} />
        <CountTile label="Employees" value={d.counts.employees} icon={Users} />
      </div>

      {d.species_breakdown && d.species_breakdown.length > 0 && (
        <Card className="mt-6 p-6">
          <h3 className="mb-4 font-display text-lg font-bold tracking-tight">Livestock by Species</h3>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {d.species_breakdown.map((s) => (
              <div key={s.name} className="rounded-lg border border-border bg-secondary/40 p-4" data-testid={`species-${s.name.toLowerCase()}`}>
                <p className="num font-display text-2xl font-extrabold text-primary">{s.value}</p>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">{s.name}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="p-6">
          <h3 className="mb-4 font-display text-lg font-bold tracking-tight">Recent Invoices</h3>
          {d.recent_invoices.length === 0 ? (
            <p className="text-sm text-muted-foreground">No invoices yet.</p>
          ) : (
            <div className="space-y-3">
              {d.recent_invoices.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between border-b border-border pb-3 last:border-0 last:pb-0">
                  <div>
                    <p className="text-sm font-semibold">{inv.invoice_number}</p>
                    <p className="text-xs text-muted-foreground">{inv.customer_name} · {fmtDate(inv.issue_date)}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusBadge value={inv.status} />
                    <span className="num text-sm font-semibold">{fmtMoney(inv.total)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-6">
          <h3 className="mb-4 font-display text-lg font-bold tracking-tight">Low Stock Alerts</h3>
          {d.low_stock.length === 0 ? (
            <p className="text-sm text-muted-foreground">All inventory levels are healthy. 🌾</p>
          ) : (
            <div className="space-y-3">
              {d.low_stock.map((it) => (
                <div key={it.id} className="flex items-center justify-between border-b border-border pb-3 last:border-0 last:pb-0">
                  <div>
                    <p className="text-sm font-semibold">{it.name}</p>
                    <p className="text-xs text-muted-foreground">{it.category}</p>
                  </div>
                  <span className="num rounded-md bg-red-50 px-2 py-1 text-xs font-semibold text-red-600">{it.quantity} {it.unit} left</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
